import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';

/**
 * Query param key used to carry a caller's full URL forward across a navigation,
 * so the destination page can return to the exact same place later. Exported so
 * any code that needs to inspect it directly (rather than going through the
 * service) uses the same key instead of a hardcoded string.
 */
export const RETURN_URL_QUERY_PARAM = 'returnUrl';

/**
 * Generic "go there, come back to exactly here" navigation helper.
 *
 * Any feature can use this instead of `Location.back()` (unreliable after a page
 * refresh, a direct/bookmarked URL, or when there is no browser history) or a
 * hardcoded return route. The caller captures its own current URL - path, route
 * params, query params and fragment, whatever it happens to be - before
 * navigating forward; the destination reads it back and returns to that exact
 * URL, with no knowledge of what that URL contains.
 */
@Injectable({
  providedIn: 'root',
})
export class ReturnNavigationService {

  constructor(private router: Router) {}

  /**
   * Navigate to `commands`, carrying the current URL along as a `returnUrl`
   * query param so the destination can navigate back to it later via
   * `navigateBack()`.
   */
  navigateWithReturnUrl(commands: any[], extras: NavigationExtras = {}): Promise<boolean> {
    const returnUrl = this.router.url;
    return this.router.navigate(commands, {
      ...extras,
      queryParams: {
        ...(extras.queryParams || {}),
        [RETURN_URL_QUERY_PARAM]: returnUrl,
      },
    });
  }

  /**
   * Navigate back to the URL captured by `navigateWithReturnUrl`, read from
   * `route`'s current query params. Calls `fallback` instead if no return URL
   * was captured (e.g. this page was opened directly, or by a caller that
   * hasn't adopted this service yet) or if it isn't a safe same-app path.
   */
  navigateBack(route: ActivatedRoute, fallback: () => void): void {
    const returnUrl = route.snapshot.queryParamMap.get(RETURN_URL_QUERY_PARAM);
    if (returnUrl && this.isSameAppUrl(returnUrl)) {
      this.router.navigateByUrl(returnUrl);
    } else {
      fallback();
    }
  }

  // Angular's router only ever resolves same-app routes anyway (it can't navigate
  // the browser to an external origin), but a malformed or foreign-looking value
  // is rejected up front rather than handed to navigateByUrl.
  private isSameAppUrl(url: string): boolean {
    return url.startsWith('/') && !url.startsWith('//');
  }
}
